import React, { useState } from 'react'
import { FontIcon, tokens } from '@arvoreeducacao/bonsai-ds'
import type { UserNotification } from '../types'

interface NotificationItemProps {
  notification: UserNotification
  onRead: (id: string) => void
  onDismiss: (id: string) => void
  onClick: (notification: UserNotification) => void
}

const getRelativeTime = (dateString: string): string => {
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMinutes < 1) return 'agora'
  if (diffMinutes < 60) return `há ${diffMinutes}min`
  if (diffHours < 24) return `há ${diffHours}h`
  if (diffDays === 1) return 'ontem'
  if (diffDays < 7) return `há ${diffDays}d`
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

const MegaphoneIcon: React.FC<{ size: number; color: string }> = ({
  size,
  color
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
    <path d="M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14" />
    <path d="M8 6v8" />
  </svg>
)

const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  read: 'book',
  indication: 'users',
  activity: 'clipboard-content'
}

const NotificationTypeIcon: React.FC<{ notificationType: string | null }> = ({
  notificationType
}) => {
  const bonsaiIcon = notificationType
    ? NOTIFICATION_TYPE_ICONS[notificationType]
    : null

  return (
    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F1F7FB] shrink-0">
      {bonsaiIcon ? (
        <FontIcon
          iconName={bonsaiIcon as 'book'}
          size={16}
          color={tokens.colors.dark5}
        />
      ) : (
        <MegaphoneIcon size={16} color={tokens.colors.dark5} />
      )}
    </div>
  )
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
  onDismiss,
  onClick
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const hasLink = Boolean(
    notification.data?.deep_link || notification.data?.url
  )

  const handleClick = () => {
    if (!notification.isRead) {
      onRead(notification.id)
    }
    if (!hasLink) {
      setIsExpanded(previous => !previous)
    }
    onClick(notification)
  }

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRead(notification.id)
  }

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDismiss(notification.id)
  }

  return (
    <div
      className={`flex items-start gap-2 px-3 py-2.5 cursor-pointer border-b border-[#DEE1E6] last:border-b-0 transition-colors hover:bg-[#DEE1E6]/40 ${
        notification.isRead ? 'bg-white' : 'bg-[#F1F7FB]'
      }`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      aria-expanded={hasLink ? undefined : isExpanded}
      aria-label={`${notification.title}${
        notification.isRead ? '' : ' - não lida'
      }`}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      {notification.isRead ? (
        <div className="min-w-2 w-2 h-8" />
      ) : (
        <div className="min-w-2 w-2 h-2 rounded-full bg-[#40CAB6] mt-3" />
      )}

      <NotificationTypeIcon notificationType={notification.notificationType} />

      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span
          className={`text-sm ${
            notification.isRead ? 'font-regular' : 'font-bold'
          } text-[#053B4B] leading-tight`}
        >
          {notification.title}
        </span>
        {notification.body && (
          <span
            className={`text-sm text-[#45717D] leading-tight ${
              isExpanded ? 'line-clamp-none' : 'line-clamp-2'
            }`}
          >
            {notification.body}
          </span>
        )}
        <span className="text-xs text-[#B7C7CB] leading-tight mt-0.5">
          {getRelativeTime(notification.createdAt)}
        </span>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!notification.isRead && (
          <button
            onClick={handleMarkAsRead}
            aria-label="Marcar como lida"
            className={`p-1 rounded-full border-none bg-transparent cursor-pointer transition-colors hover:bg-[#F1F7FB] max-[992px]:visible ${
              isHovered ? 'visible' : 'invisible max-[992px]:visible'
            }`}
          >
            <FontIcon iconName="check" size={14} color={tokens.colors.dark5} />
          </button>
        )}
        {notification.isRead && (
          <button
            onClick={handleDismiss}
            aria-label="Dispensar notificação"
            className={`p-1 rounded-full border-none bg-transparent cursor-pointer transition-colors hover:bg-[#FDEAEA] max-[992px]:visible ${
              isHovered ? 'visible' : 'invisible max-[992px]:visible'
            }`}
          >
            <FontIcon iconName="trash" size={14} color="#f95050" />
          </button>
        )}
      </div>
    </div>
  )
}

export default NotificationItem
